import { useEffect, useRef, useState } from 'react';

import type { IMessageElement } from '@chainlit/react-client';

import { Markdown } from '@/components/Markdown';

interface StreamingMarkdownProps {
    targetContent: string;
    streaming: boolean;
    allowHtml?: boolean;
    latex?: boolean;
    refElements?: IMessageElement[];
}

/**
 * Normal typewriter reveal speed (chars per rAF frame, ~60fps).
 * 4 chars/frame ≈ 240 chars/sec — fast enough to feel smooth, slow enough to read.
 */
const TYPEWRITER_CHARS_PER_FRAME = 1;
/**
 * If the backlog exceeds this, start catching up to avoid an end-of-stream dump.
 * Below this threshold we always use TYPEWRITER_CHARS_PER_FRAME.
 */
const BACKLOG_CATCH_UP_THRESHOLD = 40;
/**
 * How many frames to spread drain over (after streaming ends).
 */
const DRAIN_CATCH_UP_FRAMES = 4;

/**
 * Typewriter-style streaming reveal.
 *
 * Reveals at a fixed comfortable pace (TYPEWRITER_CHARS_PER_FRAME) so the
 * typewriter effect is always visible. Only speeds up when the incoming buffer
 * grows beyond BACKLOG_CATCH_UP_THRESHOLD to prevent a dump at the end.
 */
export function StreamingMarkdown({
    targetContent,
    streaming,
    allowHtml,
    latex,
    refElements
}: StreamingMarkdownProps) {
    const [revealedLength, setRevealedLength] = useState(0);
    const rafRef = useRef<number | null>(null);
    const targetRef = useRef(targetContent);
    const streamingRef = useRef(streaming);
    const doneRef = useRef(false);
    targetRef.current = targetContent;
    streamingRef.current = streaming;

    useEffect(() => {
        if (streaming) {
            setRevealedLength(0);
            doneRef.current = false;
        }
    }, [streaming]);

    useEffect(() => {
        if (doneRef.current) return;

        const tick = () => {
            const target = targetRef.current;
            const isDraining = !streamingRef.current;

            setRevealedLength((prev) => {
                if (prev >= target.length) {
                    if (isDraining) doneRef.current = true;
                    return prev;
                }

                const remaining = target.length - prev;

                if (isDraining) {
                    // Spread remaining evenly over DRAIN_CATCH_UP_FRAMES frames
                    return Math.min(
                        prev +
                            Math.max(
                                1,
                                Math.ceil(remaining / DRAIN_CATCH_UP_FRAMES)
                            ),
                        target.length
                    );
                }

                // Normal typewriter pace
                let step = TYPEWRITER_CHARS_PER_FRAME;

                // If backlog is growing too large, speed up proportionally to
                // prevent accumulated text dumping at end
                if (remaining > BACKLOG_CATCH_UP_THRESHOLD) {
                    const excess = remaining - BACKLOG_CATCH_UP_THRESHOLD;
                    step = TYPEWRITER_CHARS_PER_FRAME + Math.ceil(excess / 20);
                }

                return Math.min(prev + step, target.length);
            });

            rafRef.current = requestAnimationFrame(tick);
        };

        rafRef.current = requestAnimationFrame(tick);

        return () => {
            if (rafRef.current !== null) {
                cancelAnimationFrame(rafRef.current);
                rafRef.current = null;
            }
        };
    }, [streaming]);

    const revealedText = targetContent.slice(0, revealedLength);

    return (
        <Markdown allowHtml={allowHtml} latex={latex} refElements={refElements}>
            {revealedText}
        </Markdown>
    );
}
